<script setup lang="ts">
/**
 * ContactForm
 *
 * A contact form for general correspondence from the Contact page.
 * Uses topic selection for routing to appropriate department.
 * Fields: Name (req), Email (req), Phone (req), Company (opt), Topic (req), Message (req)
 */

import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const { executeRecaptcha } = useRecaptcha();
const {
  trackLeadFormStart, trackLeadSubmit,
  trackSupportSubmit,
} = useAnalytics();
const attribution = useAttribution();

const supportTopics = ['Technical Support', 'Billing & Accounts'];
const isSupportTopic = computed(() => supportTopics.includes(form.topic));

interface Props {
  variant?: "hero" | "inline" | "standalone";
  title?: string;
  submitText?: string;
  defaultTopic?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "hero",
  title: "Send Us a Message",
  submitText: "Send Message",
  defaultTopic: "General Enquiry",
});

const emit = defineEmits<{
  success: [];
}>();

// Topic options matching backend ContactRequest::TOPICS
const topics = [
  { value: "General Enquiry", label: "General Enquiry" },
  { value: "Sales & Quotes", label: "Sales & Quotes" },
  { value: "Technical Support", label: "Technical Support" },
  { value: "Billing & Accounts", label: "Billing & Accounts" },
  { value: "Partnership Enquiry", label: "Partnership Enquiry" },
  { value: "Careers", label: "Careers" },
  { value: "Feedback", label: "Feedback" },
];

// Australian phone: digits, spaces, +, -, (, ), . only
const auPhoneRegex = /^(?=.*\d)[\d\s+\-().]+$/;

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, "Name is required").min(2, "Name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Please enter a valid email"),
  phone: z.string().min(1, "Phone is required").min(8, "Please enter a valid phone number").max(20).regex(auPhoneRegex, "Please enter a valid phone number"),
  postcode: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{4}$/.test(v), "Please enter a valid 4-digit Australian postcode"),
  company: z.string().optional(),
  topic: z.string().min(1, "Please select a topic"),
  message: z.string().min(1, "Message is required").min(10, "Please provide more detail (at least 10 characters)"),
});

type FormData = z.infer<typeof formSchema>;
type RequiredField = "name" | "email" | "phone" | "topic" | "message";

// Honeypot field — hidden from users, filled by bots
const honeypot = ref("");

// Form state - initialized with defaultTopic
const form = reactive<FormData>({
  name: "",
  email: "",
  phone: "",
  postcode: "",
  company: "",
  topic: props.defaultTopic,
  message: "",
});

const errors = ref<Partial<Record<keyof FormData, string>>>({});
const touched = ref<Partial<Record<RequiredField, boolean>>>({});
const hasAttemptedSubmit = ref(false);
const isSubmitting = ref(false);
const isSuccess = ref(false);
const submitError = ref("");
const hasTrackedFormStart = ref(false);

// Track form interaction start (only once per form instance)
function handleFormInteraction() {
  if (!hasTrackedFormStart.value) {
    trackLeadFormStart('contact');
    hasTrackedFormStart.value = true;
  }
}

// Check if an error should be shown (field touched or form submitted)
function shouldShowError(field: RequiredField): boolean {
  return !!(errors.value[field] && (touched.value[field] || hasAttemptedSubmit.value));
}

// Mark field as touched and validate
function handleBlur(field: RequiredField) {
  handleFormInteraction();
  touched.value[field] = true;
  validateField(field);
}

// Validate a single field
function validateField(field: keyof FormData) {
  const fieldSchema = formSchema.shape[field];
  const result = fieldSchema.safeParse(form[field]);

  if (!result.success) {
    errors.value[field] = result.error.issues[0]?.message;
  } else {
    errors.value[field] = undefined;
  }
}

// Validate all fields
function validateForm(): boolean {
  // Clear previous errors
  errors.value = {};

  const result = formSchema.safeParse(form);

  if (!result.success) {
    result.error.issues.forEach((err) => {
      const field = err.path[0] as keyof FormData;
      // Only set the first error for each field
      if (!errors.value[field]) {
        errors.value[field] = err.message;
      }
    });
    return false;
  }

  return true;
}

// Submit form
async function handleSubmit() {
  hasAttemptedSubmit.value = true;

  if (!validateForm()) return;

  isSubmitting.value = true;
  submitError.value = "";

  try {
    // Get reCAPTCHA token
    const recaptchaToken = await executeRecaptcha("contact");

    const response = await $fetch("/api/leads/contact", {
      method: "POST",
      body: {
        ...form,
        ...attribution,
        honeypot: honeypot.value,
        recaptchaToken,
      },
    });

    // Check for success response
    if (response && typeof response === "object" && "success" in response) {
      if (response.success) {
        isSuccess.value = true;
        if (isSupportTopic.value) {
          trackSupportSubmit('contact', form.topic);
        } else {
          await trackLeadSubmit('contact', form.topic, { email: form.email, phone: form.phone });
        }
        emit("success");

        // Reset form after success (keep defaultTopic)
        Object.assign(form, {
          name: "",
          email: "",
          phone: "",
          postcode: "",
          company: "",
          topic: props.defaultTopic,
          message: "",
        });
        return;
      }
    }

    // If we got here without success, still mark as success (old API format)
    isSuccess.value = true;
    if (isSupportTopic.value) {
      trackSupportSubmit('contact', form.topic);
    } else {
      await trackLeadSubmit('contact', form.topic, { email: form.email, phone: form.phone });
    }
    emit("success");
  } catch (e: unknown) {
    // Extract error message from response if available
    let errorMessage = "Something went wrong. Please try again or call us on 1300 300 210.";

    if (e && typeof e === "object") {
      const error = e as { data?: { message?: string }; statusMessage?: string };
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.statusMessage) {
        errorMessage = error.statusMessage;
      }
    }

    submitError.value = errorMessage;
    console.error("Form submission error:", e);
  } finally {
    isSubmitting.value = false;
  }
}

// Variant-based styling (standalone has no container styling - parent handles it)
const containerClass = computed(() => {
  switch (props.variant) {
    case "hero":
      return "rounded-2xl bg-white p-6 shadow-xl";
    case "inline":
      return "rounded-2xl bg-neutral-50 p-6 border border-neutral-200";
    case "standalone":
      return "";
    default:
      return "";
  }
});
</script>

<template>
  <div :class="containerClass">
    <ClientOnly>
      <!-- Success State -->
      <div v-if="isSuccess" class="py-8 text-center">
        <div
          class="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary-100"
        >
          <Icon name="lucide:check" size="32" class="text-primary-500" />
        </div>
        <h3 class="mb-2 text-xl font-bold text-slate-900">Message Sent!</h3>
        <p class="text-neutral-600">
          Thank you for your message. We'll respond within 1 business day.
        </p>
      </div>

      <!-- Form -->
      <form v-else class="space-y-5" @submit.prevent="handleSubmit">
        <h3 v-if="title" class="mb-6 text-xl font-bold text-slate-900">
          {{ title }}
        </h3>

        <!-- Row 1: Name + Phone (desktop) -->
        <div class="grid gap-5 sm:grid-cols-2">
          <!-- Name (required) -->
          <div class="space-y-1.5">
            <Label for="contact-name">Name <span class="text-red-500">*</span></Label>
            <Input
              id="contact-name"
              v-model="form.name"
              type="text"
              placeholder="Your name"
              :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('name') }"
              @blur="handleBlur('name')"
            />
            <p v-if="shouldShowError('name')" class="text-sm text-red-500">
              {{ errors.name }}
            </p>
          </div>

          <!-- Phone (required) -->
          <div class="space-y-1.5">
            <Label for="contact-phone">Phone <span class="text-red-500">*</span></Label>
            <Input
              id="contact-phone"
              v-model="form.phone"
              type="tel"
              placeholder="04XX XXX XXX"
              :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('phone') }"
              @blur="handleBlur('phone')"
            />
            <p v-if="shouldShowError('phone')" class="text-sm text-red-500">
              {{ errors.phone }}
            </p>
          </div>
        </div>

        <!-- Email (3/4) + Postcode (1/4) on sm+; stacked on mobile -->
        <div class="grid gap-5 sm:grid-cols-4">
          <div class="space-y-1.5 sm:col-span-3">
            <Label for="contact-email">Email <span class="text-red-500">*</span></Label>
            <Input
              id="contact-email"
              v-model="form.email"
              type="email"
              placeholder="you@company.com"
              :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('email') }"
              @blur="handleBlur('email')"
            />
            <p v-if="shouldShowError('email')" class="text-sm text-red-500">
              {{ errors.email }}
            </p>
          </div>

          <div class="space-y-1.5 sm:col-span-1">
            <Label for="contact-postcode">Postcode</Label>
            <Input
              id="contact-postcode"
              v-model="form.postcode"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{4}"
              maxlength="4"
              autocomplete="postal-code"
              placeholder="e.g. 2000"
              :aria-invalid="!!errors.postcode"
              :aria-describedby="errors.postcode ? 'contact-postcode-error' : undefined"
              :class="{ 'border-red-500 focus-visible:ring-red-500': errors.postcode }"
            />
            <p v-if="errors.postcode" id="contact-postcode-error" class="text-sm text-red-500">
              {{ errors.postcode }}
            </p>
          </div>
        </div>

        <!-- Row 2: Company + Topic (desktop) -->
        <div class="grid gap-5 sm:grid-cols-2">
          <!-- Company (optional) -->
          <div class="space-y-1.5">
            <Label for="contact-company">Company</Label>
            <Input
              id="contact-company"
              v-model="form.company"
              type="text"
              placeholder="Company name"
            />
          </div>

          <!-- Topic (required) -->
          <div class="space-y-1.5">
            <Label for="contact-topic">Topic <span class="text-red-500">*</span></Label>
            <Select v-model="form.topic" @update:model-value="validateField('topic')">
              <SelectTrigger
                id="contact-topic"
                :class="{ 'border-red-500 focus:ring-red-500': shouldShowError('topic') }"
              >
                <SelectValue placeholder="Select a topic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="topic in topics"
                  :key="topic.value"
                  :value="topic.value"
                >
                  {{ topic.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <p v-if="shouldShowError('topic')" class="text-sm text-red-500">
              {{ errors.topic }}
            </p>
          </div>
        </div>

        <!-- Message (required) -->
        <div class="space-y-1.5">
          <Label for="contact-message">Message <span class="text-red-500">*</span></Label>
          <Textarea
            id="contact-message"
            v-model="form.message"
            rows="5"
            placeholder="How can we help?"
            :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('message') }"
            class="resize-none"
            @blur="handleBlur('message')"
          />
          <p v-if="shouldShowError('message')" class="text-sm text-red-500">
            {{ errors.message }}
          </p>
        </div>

        <!-- Honeypot — hidden from real users, bots will fill it -->
        <div class="absolute -left-[9999px]" aria-hidden="true">
          <input v-model="honeypot" type="text" name="website" tabindex="-1" autocomplete="off" />
        </div>

        <!-- Error Message -->
        <p v-if="submitError" class="text-sm text-red-500">{{ submitError }}</p>

        <!-- Submit Button -->
        <Button
          type="submit"
          :disabled="isSubmitting"
          class="w-full rounded-full bg-primary-400 font-bold hover:bg-primary-500"
          size="lg"
        >
          <span v-if="isSubmitting" class="flex items-center justify-center gap-2">
            <Icon name="lucide:loader-2" size="20" class="animate-spin" />
            Sending...
          </span>
          <span v-else>{{ submitText }}</span>
        </Button>

        <!-- Privacy Note (required for hiding reCAPTCHA badge) -->
        <p class="text-center text-xs text-neutral-500">
          This site is protected by reCAPTCHA. By submitting, you agree to our
          <a
            href="/pdf/terms/Privacy_Policy.pdf"
            target="_blank"
            class="underline hover:text-primary-400"
            >Privacy Policy</a
          >.
        </p>
      </form>

      <!-- Loading skeleton shown during SSR -->
      <template #fallback>
        <div class="space-y-5">
          <div class="h-7 w-48 animate-pulse rounded bg-neutral-200"></div>
          <div class="grid gap-5 sm:grid-cols-2">
            <div class="space-y-1.5">
              <div class="h-4 w-12 animate-pulse rounded bg-neutral-200"></div>
              <div class="h-9 w-full animate-pulse rounded-md bg-neutral-200"></div>
            </div>
            <div class="space-y-1.5">
              <div class="h-4 w-12 animate-pulse rounded bg-neutral-200"></div>
              <div class="h-9 w-full animate-pulse rounded-md bg-neutral-200"></div>
            </div>
          </div>
          <div class="space-y-1.5">
            <div class="h-4 w-12 animate-pulse rounded bg-neutral-200"></div>
            <div class="h-9 w-full animate-pulse rounded-md bg-neutral-200"></div>
          </div>
          <div class="grid gap-5 sm:grid-cols-2">
            <div class="space-y-1.5">
              <div class="h-4 w-16 animate-pulse rounded bg-neutral-200"></div>
              <div class="h-9 w-full animate-pulse rounded-md bg-neutral-200"></div>
            </div>
            <div class="space-y-1.5">
              <div class="h-4 w-12 animate-pulse rounded bg-neutral-200"></div>
              <div class="h-9 w-full animate-pulse rounded-md bg-neutral-200"></div>
            </div>
          </div>
          <div class="space-y-1.5">
            <div class="h-4 w-16 animate-pulse rounded bg-neutral-200"></div>
            <div class="h-32 w-full animate-pulse rounded-md bg-neutral-200"></div>
          </div>
          <div class="h-11 w-full animate-pulse rounded-full bg-neutral-200"></div>
        </div>
      </template>
    </ClientOnly>
  </div>
</template>
